import { useState } from 'react';

import { CHANNEL_OPTIONS, CHANNEL_LABEL } from '../utils/format';

import '../styles/notifications.css';

// Campos compartidos por crear/editar plantilla. Los errores se pintan inline
// (validación client-side + ApiError.fields). En edición, `code` y `channel`
// son de solo lectura: el contrato PATCH solo permite name/provider_sid/
// parameters/trigger/status (el código es el identificador; el canal se fija al crear).

// Chip-input para `parameters` (array de nombres de variables de la plantilla).
function ParamsInput({ values, onChange, disabled }) {
    const [draft, setDraft] = useState('');

    const add = () => {
        const token = draft.trim();
        if (!token) return;
        if (!values.includes(token)) onChange([...values, token]);
        setDraft('');
    };

    const remove = (token) => onChange(values.filter((v) => v !== token));

    const onKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add();
        } else if (e.key === 'Backspace' && !draft && values.length) {
            remove(values[values.length - 1]);
        }
    };

    return (
        <div className="ntf-params">
            {values.length > 0 && (
                <ul className="ntf-params__list">
                    {values.map((p) => (
                        <li key={p} className="ntf-chip">
                            <span className="ntf-chip__text">{p}</span>
                            {!disabled && (
                                <button
                                    type="button"
                                    className="ntf-chip__remove"
                                    onClick={() => remove(p)}
                                    aria-label={`Quitar ${p}`}
                                >×</button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
            <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                onBlur={add}
                disabled={disabled}
                placeholder="Escribí una variable y Enter (p. ej. nombre_cliente)"
            />
        </div>
    );
}

export default function TemplateFields({ form, setField, fieldError, disabled, isEdit = false }) {
    return (
        <div className="cat-form__grid">
            <label className="cat-form__field">
                <span className="cat-form__label">Código</span>
                {isEdit ? (
                    <span className="ntf-readonly ntf-code">{form.code}</span>
                ) : (
                    <input
                        type="text"
                        value={form.code}
                        onChange={(e) => setField('code', e.target.value.toUpperCase())}
                        maxLength={60}
                        disabled={disabled}
                        placeholder="PARTO_REGISTRADO"
                        autoFocus
                        aria-invalid={!!fieldError('code')}
                    />
                )}
                {fieldError('code') && <span className="cat-form__error" role="alert">{fieldError('code')}</span>}
                {!isEdit && <span className="cat-form__hint">Identificador único e inmutable. Mayúsculas, números y _.</span>}
            </label>

            <label className="cat-form__field">
                <span className="cat-form__label">Canal</span>
                {isEdit ? (
                    <span className="ntf-readonly">{CHANNEL_LABEL[form.channel] || form.channel || '—'}</span>
                ) : (
                    <select
                        value={form.channel}
                        onChange={(e) => setField('channel', e.target.value)}
                        disabled={disabled}
                    >
                        {CHANNEL_OPTIONS.map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                )}
            </label>

            <label className="cat-form__field cat-form__field--full">
                <span className="cat-form__label">Nombre</span>
                <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    maxLength={120}
                    disabled={disabled}
                    placeholder="Parto registrado"
                    autoFocus={isEdit}
                    aria-invalid={!!fieldError('name')}
                />
                {fieldError('name') && <span className="cat-form__error" role="alert">{fieldError('name')}</span>}
            </label>

            <label className="cat-form__field">
                <span className="cat-form__label">Trigger</span>
                <input
                    type="text"
                    value={form.trigger}
                    onChange={(e) => setField('trigger', e.target.value)}
                    maxLength={60}
                    disabled={disabled}
                    placeholder="Opcional"
                    aria-invalid={!!fieldError('trigger')}
                />
                {fieldError('trigger') && <span className="cat-form__error" role="alert">{fieldError('trigger')}</span>}
                <span className="cat-form__hint">Evento que dispara el envío automático. Vacío = manual / comunicado.</span>
            </label>

            <label className="cat-form__field">
                <span className="cat-form__label">Content SID (Twilio)</span>
                <input
                    type="text"
                    value={form.provider_sid}
                    onChange={(e) => setField('provider_sid', e.target.value)}
                    maxLength={64}
                    disabled={disabled}
                    placeholder="HX… (opcional)"
                    aria-invalid={!!fieldError('provider_sid')}
                />
                {fieldError('provider_sid') && <span className="cat-form__error" role="alert">{fieldError('provider_sid')}</span>}
                <span className="cat-form__hint">El cuerpo aprobado (Meta) vive en Twilio; acá solo el HX.</span>
            </label>

            <label className="cat-form__field cat-form__field--full">
                <span className="cat-form__label">Variables (parameters)</span>
                <ParamsInput
                    values={form.parameters}
                    onChange={(next) => setField('parameters', next)}
                    disabled={disabled}
                />
                <span className="cat-form__hint">Nombres de las variables que usa la plantilla, en orden.</span>
            </label>
        </div>
    );
}
